import { type KeyboardEvent as ReactKeyboardEvent, type RefObject, useEffect, useRef } from 'react';

interface UseDrawerAccessibilityParams {
  drawerRef: RefObject<HTMLDivElement | null>;
  isOpen: boolean;
  shouldRender: boolean;
  onClose: VoidFunction;
}

interface UseDrawerAccessibilityResult {
  handleDrawerKeyDown: (event: ReactKeyboardEvent<HTMLDivElement>) => void;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

const useDrawerAccessibility = ({
  drawerRef,
  isOpen,
  shouldRender,
  onClose,
}: UseDrawerAccessibilityParams): UseDrawerAccessibilityResult => {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen || !shouldRender || !drawerRef.current) return;

    const drawerElement = drawerRef.current;
    const focusableElements = drawerElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);

    focusableElements[0]?.focus();

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [drawerRef, isOpen, shouldRender]);

  const handleDrawerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;

    const focusableElements = event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  return { handleDrawerKeyDown };
};

export default useDrawerAccessibility;
